import { Link, NavLink } from "react-router-dom";
import { message } from "antd";

import DescriptionIcon from '@mui/icons-material/Description';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import GroupIcon from '@mui/icons-material/Group';
import PaymentsIcon from '@mui/icons-material/Payments';
import BarChartIcon from '@mui/icons-material/BarChart';

import useHomeData from './useHomeData';
import { ENABLED_MOBILE_ROUTES } from './enabledRoutes';
import { formatKes, initialsOf } from './format';
import logo from '../../assets/riba-logo.svg';
import './MobileHome.css';

// Keyed by status code. Red marks what still needs review; closed-out
// applications are muted.
const STATUS_COLORS = {
    PENDING: "#e8452c",
    APPROVED: "#1a9e6b",
    DECLINED: "#8fa7bd",
    WITHDRAWN: "#8fa7bd",
};


const activeLoansLabel = (count) => {
    if (count === 0) return "No active loans";
    if (count === 1) return "1 active loan";
    return `${count} active loans`;
};

const SearchIcon = () => (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
        <circle cx="8.5" cy="8.5" r="5.5" stroke="#1b4965" strokeWidth="2" />
        <line x1="13" y1="13" x2="17.5" y2="17.5" stroke="#1b4965" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

const comingSoon = () => message.info('Coming soon');

/* Gated destinations render as buttons rather than links so a
   long-press "open in new tab" can't walk past the gate. */
const QuickLink = ({ to, className, children }) => {
    if (to && ENABLED_MOBILE_ROUTES.includes(to)) {
        return <NavLink to={to} className={className}>{children}</NavLink>;
    }

    return (
        <button type="button" className={className} onClick={comingSoon}>
            {children}
        </button>
    );
};

const QuickLinkBody = ({ icon, label, sub, badge }) => (
    <>
        <span className="mobile-home-tile">{icon}</span>
        <span className="mobile-home-row-text">
            <span className="mobile-home-row-label">{label}</span>
            <span className="mobile-home-row-sub">{sub}</span>
        </span>
        {badge && <span className="mobile-home-badge">{badge}</span>}
        <span className="mobile-home-chevron">›</span>
    </>
);

const MobileHome = () => {
    const { isLoading, isError, refetch, user, portfolio, counts, recentApplications } = useHomeData();

    return (
        <div className="mobile-home">
            <header className="mobile-home-header">
                <img className="mobile-home-logo" src={logo} alt="riba" />
                <div className="mobile-home-header-actions">
                    {/* Search screen is not designed yet — no action. */}
                    <button type="button" className="mobile-home-search">
                        <SearchIcon />
                    </button>
                    <span className="mobile-home-avatar">{user.initials}</span>
                </div>
            </header>

            <div className="mobile-home-content">
                {isError ? (
                    <div className="mobile-home-error">
                        Couldn't load dashboard
                        <button
                            type="button"
                            className="mobile-home-retry"
                            onClick={() => refetch()}
                        >
                            Retry
                        </button>
                    </div>
                ) : (
                    <section className="mobile-home-portfolio">
                        <div className="mobile-home-portfolio-label">Portfolio outstanding</div>
                        {isLoading ? (
                            <>
                                <span className="mobile-home-skeleton value" />
                                <span className="mobile-home-skeleton" />
                            </>
                        ) : (
                            <>
                                <div className="mobile-home-portfolio-value">{formatKes(portfolio.outstanding)}</div>
                                <div className="mobile-home-portfolio-meta">
                                    {/* No due instalments means no rate — show nothing, not 0%. */}
                                    {portfolio.onTimePct !== null && (
                                        <span className="mobile-home-ontime">▲ {portfolio.onTimePct}% on time</span>
                                    )}
                                    <span className="mobile-home-active">{activeLoansLabel(portfolio.activeLoans)}</span>
                                </div>
                            </>
                        )}
                    </section>
                )}

                <nav className="mobile-home-links">
                    <QuickLink to="/dashboard/applications" className="mobile-home-row">
                        <QuickLinkBody
                            icon={<DescriptionIcon className="mobile-home-tile-icon" />}
                            label="Applications"
                            sub="Review & approve"
                            badge={counts?.newApplications > 0 ? `${counts.newApplications} new` : null}
                        />
                    </QuickLink>

                    <QuickLink to="/dashboard/loans" className="mobile-home-row">
                        <QuickLinkBody
                            icon={<CreditScoreIcon className="mobile-home-tile-icon" />}
                            label="Loans"
                            sub="Balances & schedules"
                        />
                    </QuickLink>

                    <QuickLink to="/dashboard/customers" className="mobile-home-row">
                        <QuickLinkBody
                            icon={<GroupIcon className="mobile-home-tile-icon" />}
                            label="Customers"
                            sub={counts && `${counts.customers} customers`}
                        />
                    </QuickLink>

                    <QuickLink to="/dashboard/payments" className="mobile-home-row">
                        <QuickLinkBody
                            icon={<PaymentsIcon className="mobile-home-tile-icon" />}
                            label="Payments"
                            sub="Repayments & receipts"
                        />
                    </QuickLink>

                    {/* No reports route exists yet, so no path to gate on. */}
                    <QuickLink className="mobile-home-row">
                        <QuickLinkBody
                            icon={<BarChartIcon className="mobile-home-tile-icon" />}
                            label="Reports"
                            sub="Collections, arrears"
                        />
                    </QuickLink>
                </nav>

                <section className="mobile-home-recent">
                    <div className="mobile-home-recent-header">
                        <span className="mobile-home-recent-title">Recent applications</span>
                        <QuickLink to="/dashboard/applications" className="mobile-home-viewall">View all</QuickLink>
                    </div>

                    {isLoading && [1, 2].map(n => (
                        <div className="mobile-home-app" key={n}>
                            <span className="mobile-home-app-avatar" />
                            <span className="mobile-home-app-text">
                                <span className="mobile-home-skeleton wide" />
                                <span className="mobile-home-skeleton" />
                            </span>
                        </div>
                    ))}

                    {recentApplications && recentApplications.length === 0 && (
                        <div className="mobile-home-empty">No applications yet</div>
                    )}

                    {recentApplications && recentApplications.map(application => (
                        <Link
                            to={`/dashboard/application/${application.id}`}
                            className="mobile-home-app"
                            key={application.id}
                        >
                            <span className="mobile-home-app-avatar">{initialsOf(application.name)}</span>
                            <span className="mobile-home-app-text">
                                <span className="mobile-home-app-name">{application.name}</span>
                                <span className="mobile-home-app-detail">
                                    {application.product} · {formatKes(application.amount)}
                                </span>
                            </span>
                            <span
                                className="mobile-home-app-status"
                                style={{ color: STATUS_COLORS[application.status] }}
                            >
                                {application.statusLabel}
                            </span>
                        </Link>
                    ))}
                </section>
            </div>
        </div>
    );
};

export default MobileHome;
